import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

interface UpdateInsuranceCompanyModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (payerId: string, payerName: string) => void;
  supportedPayers: CredentialsSupportedPayersMessage[];
  currentCompanyName: string | null;
  currentPayerName: string | null;
  saving: boolean;
}

export default function UpdateInsuranceCompanyModal({
  open,
  onClose,
  onSave,
  supportedPayers,
  currentCompanyName,
  currentPayerName,
  saving,
}: UpdateInsuranceCompanyModalProps) {
  const [selectedPayer, setSelectedPayer] =
    useState<CredentialsSupportedPayersMessage | null>(null);

  useEffect(() => {
    if (open && currentPayerName) {
      const match = supportedPayers.find(
        (p) =>
          p.displayLabel === currentPayerName || p.name === currentPayerName,
      );
      setSelectedPayer(match || null);
    } else if (open) {
      setSelectedPayer(null);
    }
  }, [open, currentPayerName, supportedPayers]);

  const handleSave = () => {
    if (selectedPayer) {
      onSave(selectedPayer.wieldyId, selectedPayer.displayLabel);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="updateInsuranceCompanyModal"
    >
      <DialogTitle>Tag Wieldy Payer Name</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mb: 2.5,
            py: 1.5,
            px: 2,
            backgroundColor: "grey.50",
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            PMS Insurance Name
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {currentCompanyName || "N/A"}
          </Typography>
        </Box>

        <Autocomplete
          data-testid="payerAutocomplete"
          options={supportedPayers}
          getOptionLabel={(option) => option.displayLabel}
          value={selectedPayer}
          onChange={(_event, newValue) => {
            setSelectedPayer(newValue);
          }}
          filterOptions={(options, { inputValue }) => {
            const filterValue = inputValue.toLowerCase();
            return options.filter(
              (option) =>
                option.displayLabel.toLowerCase().includes(filterValue) ||
                option.name.toLowerCase().includes(filterValue),
            );
          }}
          renderInput={(params) => {
            const combinedInputProps = {
              ...params.InputProps,
              inputProps: params.inputProps,
            };
            return (
              <TextField
                id={params.id}
                disabled={params.disabled}
                fullWidth={params.fullWidth}
                size={params.size}
                InputProps={combinedInputProps}
                label="Search Wieldy Payer Name"
                variant="outlined"
              />
            );
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          data-testid="payerModalCancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!selectedPayer || saving}
          data-testid="payerModalSave"
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
